/**
 * Assessment Details timeline (checkbox-style, non-linear).
 * Ticket 14744 — design from prototype; step names from status table.
 */

export const TIMELINE_NODE = {
  COMPLETED: "completed",
  CURRENT: "current",
  PENDING: "pending",
};

/** Steps shown on the Assessment Details progress timeline. */
export const ASSESSMENT_TIMELINE_STEPS = [
  "Pending Diligent Review",
  "Request Accepted",
  "Assessment Scheduled",
  "Scope Defined",
  "Assessment Complete",
  "In Sponsor Review",
  "Final Report Delivered",
  "Pending Observation Response",
  "Cancelled",
  "Assessment Closed",
];

/**
 * Map any known/legacy status string → timeline step (or null if off-timeline).
 * @param {string|null|undefined} status
 * @returns {string|null}
 */
export function mapStatusToTimelineStep(status) {
  if (status == null || status === "") return null;
  const s = String(status).trim();

  const direct = {
    "Pending Diligent Review": "Pending Diligent Review",
    "No status selected": "Pending Diligent Review",
    "Request Accepted": "Request Accepted",
    Accepted: "Request Accepted",
    "Intro Email Sent": "Request Accepted",
    "Intro Meeting Complete": "Request Accepted",
    "Planning Meeting Complete": "Scope Defined",
    "Scope Defined": "Scope Defined",
    "Auditor Assigned": "Assessment Scheduled",
    "Assessment Scheduled": "Assessment Scheduled",
    Scheduled: "Assessment Scheduled",
    "Assessment in Progress": "Assessment Scheduled",
    "Assessment Complete": "Assessment Complete",
    "Assessment Completed": "Assessment Complete",
    "In Peer Review": "In Sponsor Review",
    "Draft Report Delivered": "In Sponsor Review",
    "Draft Review by Client": "In Sponsor Review",
    "Draft Review by Sponsor": "In Sponsor Review",
    "In Sponsor Review": "In Sponsor Review",
    "First Draft": "In Sponsor Review",
    "Final Report Delivered": "Final Report Delivered",
    "Final Report Delivered to Sponsor": "Final Report Delivered",
    "Final Report": "Final Report Delivered",
    "Pending Observation Response": "Pending Observation Response",
    "Observation Response In Review": "Pending Observation Response",
    "Report Anonymized": "Final Report Delivered",
    "Report Published to Library": "Final Report Delivered",
    "Published to Library": "Final Report Delivered",
    Cancelled: "Cancelled",
    "Canceled Assessment": "Cancelled",
    "Assessment Closed": "Assessment Closed",
  };

  return direct[s] ?? null;
}

function hasDate(value) {
  return value != null && value !== "";
}

function isPastOrToday(dateValue) {
  if (!hasDate(dateValue)) return false;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d.getTime() <= today.getTime();
}

/**
 * Field-based completion for non-linear timeline nodes.
 * A later step can be completed while an earlier one is still pending.
 *
 * @param {object|null|undefined} vqa
 * @returns {Record<string, boolean>}
 */
export function getTimelineStepCompletionFromFields(vqa) {
  const completed = Object.fromEntries(
    ASSESSMENT_TIMELINE_STEPS.map((step) => [step, false])
  );

  if (!vqa) return completed;

  const status = vqa.vqa_status ?? vqa.status;
  const mapped = mapStatusToTimelineStep(status);
  const pastPending =
    mapped != null && mapped !== "Pending Diligent Review";

  if (pastPending) {
    completed["Pending Diligent Review"] = true;
  }

  const acceptedOrBeyond =
    pastPending && mapped !== "Cancelled";

  if (
    acceptedOrBeyond ||
    status === "Request Accepted" ||
    status === "Accepted"
  ) {
    completed["Request Accepted"] = true;
  }

  if (hasDate(vqa.first_day_of_assessment) || hasDate(vqa.assessment_start_date)) {
    completed["Assessment Scheduled"] = true;
  }

  if (
    hasDate(vqa.planning_meeting_completed_on) ||
    status === "Scope Defined" ||
    status === "Planning Meeting Complete"
  ) {
    completed["Scope Defined"] = true;
  }

  if (
    isPastOrToday(vqa.assessment_completion_date) ||
    status === "Assessment Complete" ||
    status === "Assessment Completed"
  ) {
    completed["Assessment Complete"] = true;
  }

  if (
    hasDate(vqa.first_draft_report_delivered_to_sponsor_on) ||
    hasDate(vqa.draft_review_date) ||
    status === "In Sponsor Review" ||
    status === "Draft Review by Client" ||
    status === "Draft Report Delivered"
  ) {
    completed["In Sponsor Review"] = true;
  }

  if (hasDate(vqa.final_report_delivered_to_sponsor_on)) {
    completed["Final Report Delivered"] = true;
  }

  const observationNeeded =
    vqa.is_capa_needed === true || vqa.is_capa_needed === "true";
  if (
    observationNeeded &&
    hasDate(vqa.draft_report_delivered_to_provider_on)
  ) {
    completed["Pending Observation Response"] = true;
  }
  if (hasDate(vqa.observation_response_received_on)) {
    completed["Pending Observation Response"] = true;
  }

  if (
    status === "Cancelled" ||
    status === "Canceled Assessment" ||
    mapped === "Cancelled"
  ) {
    completed.Cancelled = true;
  }

  if (
    status === "Assessment Closed" ||
    hasDate(vqa.assessment_closed_on)
  ) {
    completed["Assessment Closed"] = true;
  }

  return completed;
}

/**
 * @param {{ status?: string|null, vqa?: object|null }} input
 * @returns {Array<{ step: string, state: string, lineAfterPurple: boolean }>}
 */
export function buildAssessmentTimelineNodes({ status, vqa } = {}) {
  const fieldCompleted = getTimelineStepCompletionFromFields(vqa);
  const currentStep = mapStatusToTimelineStep(status);

  return ASSESSMENT_TIMELINE_STEPS.map((step, index) => {
    const isCompleted = Boolean(fieldCompleted[step]);
    let state = TIMELINE_NODE.PENDING;
    if (isCompleted) {
      state = TIMELINE_NODE.COMPLETED;
    } else if (currentStep === step) {
      state = TIMELINE_NODE.CURRENT;
    }

    const lineAfterPurple =
      index < ASSESSMENT_TIMELINE_STEPS.length - 1 && isCompleted;

    return { step, state, lineAfterPurple };
  });
}
